import React, { useMemo } from 'react'
import clsx from 'clsx'
import i18n from '@pureartisan/simple-i18n'
import { makeStyles } from '@material-ui/core/styles'
import {
  CircularProgress,
  Box,
  Typography,
} from '@material-ui/core'
import shallow from 'zustand/shallow'
import { getStateFromFips } from '@hyperobjekt/us-states'
import useStore from '../store'
import {
  getCountyFromGeoid,
  getIdentifierPhrase,
  getVaribilityLevel,
  getSortedGrades,
  getMetroNameFromFips,
  capitalizeFirstLetter,
  isWkkfView,
} from '../utils'
import PopStack from '../PopStack'
import DomainDetailsSection from '../DomainDetailsSection'
import { PanelBody, PanelHeader } from '../SlideoutPanel'
import PanelSection from '../SlideoutPanel/PanelSection'
import Accordion from '../Accordion'
import IndexWithRating from '../IndexWithRating/IndexWithRating'
import GenericStack from '../GenericStack'
import _ from 'lodash'
import { COI_METRIC } from '../../../../constants/map'

// Styles for this component.
const useStyles = makeStyles(theme => ({
  root: {},
  locName: {
    paddingTop: theme.spacing(1),
  },
  comparedTo: {
    margin: theme.spacing(1, 0),
  },
  indicatorExplainer: {
    margin: theme.spacing(1, 0),
    fontStyle: 'italic',
  },
  childrenInfo: {
    paddingTop: theme.spacing(1),
  },
  roundedSumNote: {
    fontStyle: 'italic',
    paddingTop: theme.spacing(2),
  },
  dataTitle: {
    fontWeight: 600,
    fontSize: '18px',
    borderBottom: `1px dashed ${theme.extras.variables.colors.darkGray}`,
  },
  indexScores: {
    marginTop: theme.spacing(3),
  },
  unexpandableIndex: {
    margin: theme.spacing(2, 0),
  },
  index: {
    paddingTop: theme.spacing(3),
    borderTop: theme.extras.styles.faintBorder,
    marginTop: theme.spacing(3),
    '&.tract-panel-pad-index': {
      borderTop: 'none',
      marginTop: 0,
    },
  },
  metricTitle: {
    fontSize: '16px',
    lineHeight: '16px',
    fontWeight: 600,
    borderBottom: `1px dashed ${theme.extras.variables.colors.darkGray}`,
  },
  spinner: {
    margin: '6rem auto',
  },
  rootNoData: {
    display: 'flex',
  },

  indexSection: {
    boxShadow: 'none',

    '& .MuiAccordionSummary-root': {
      // padding: 0,
      // TODO: refactor?
      marginLeft: theme.spacing(-2.25),
      marginRight: theme.spacing(-1),
      '&:hover': {
        background: 'unset',
      },
    },
    '& .MuiAccordionSummary-content': {
      marginLeft: theme.spacing(0.5),
    },
    '& .MuiAccordionDetails-root': {
      padding: 0,
      // otherwise flex and indicator values won't align to end
      display: 'unset',
    },
  },
}))

/**
 * Displays contents specific to selected location (for display in slideout
 * panel or modal dialog)
 */
const LocationDetailsPanel = () => {
  // console.log('LocationDetailsPanel')
  const {
    slideoutPanel,
    slideoutFeature,
    allDataLoaded,
    activeNorm,
    activeGeoType,
    activeView,
    remoteJson,
    activeYear,
    redliningActive,
  } = useStore(
    state => ({
      slideoutPanel: state.slideoutPanel,
      slideoutFeature: state.slideoutFeature,
      allDataLoaded: state.allDataLoaded,
      activeNorm: state.activeNorm,
      activeGeoType: state.activeGeoType,
      activeView: state.activeView,
      remoteJson: state.remoteJson,
      activeYear: state.activeYear,
      redliningActive: state.redliningActive,
    }),
    shallow,
  )

  const classes = useStyles()

  const showPanel =
    slideoutPanel.panel === 'location' &&
    allDataLoaded &&
    !!slideoutFeature &&
    !!remoteJson &&
    !!remoteJson.pop &&
    !!remoteJson.raw

  if (!showPanel) {
    return (
      <Box
        className={clsx(
          'tract-panel-parent',
          classes.root,
          classes.rootNoData,
        )}
      >
        <CircularProgress
          aria-label="loading progress"
          color="primary"
          className={clsx(
            'tract-panel-spinner',
            classes.spinner,
          )}
        />
      </Box>
    )
  }
  const isZip = activeGeoType === 'zips'
  const isCounty = activeGeoType === 'counties'
  const feature = slideoutFeature
  // console.log('&&&&&&&', feature.properties)
  // Population feature data
  const pop = remoteJson.pop.data.find(
    el => Number(el.GEOID) === feature.id,
  )

  const countyName =
    activeGeoType === 'tracts'
      ? getCountyFromGeoid(feature.id)
      : null

  const stateName = getStateFromFips(
    String(feature.properties.s).padStart(2, '0'),
  )
  const title =
    feature.properties.m && feature.properties.m !== 0
      ? getMetroNameFromFips(feature.properties.m)
      : stateName
      ? stateName.full
      : null

  const indexMap = remoteJson.indicatorMap.data
  // console.log(indexMap, remoteJson.index_dict)

  const getNormPhrase = () => {
    switch (activeNorm) {
      case 'n':
        return i18n.translate(`SLIDEOUT_THIS_NAT`)
      case 's':
        return i18n.translate(`SLIDEOUT_THIS_STATE`, {
          state: stateName.full,
        })
      case 'm':
        return i18n.translate(`SLIDEOUT_THIS_METRO`, {
          metro: title.replace('Metro Area', ''),
        })
      default:
        return i18n.translate(`SLIDEOUT_THIS_NAT`)
    }
  }
  const thisPhrase = getNormPhrase()

  const getLocDetails = () => {
    return (
      <PanelSection>
        <Typography variant="h2">
          {getIdentifierPhrase(activeGeoType, feature.id, {
            withId: true,
          })}
        </Typography>
        <Typography
          variant="body1"
          className={classes.locName}
        >
          {title}
          {countyName ? `, ${countyName}` : null}
        </Typography>
      </PanelSection>
    )
  }

  const getPopulationBreakdown = () => {
    if (!pop) return null

    return (
      <PanelSection>
        <Typography variant="h2">
          {i18n.translate(`SLIDEOUT_CHILDREN`)}
        </Typography>
        <Typography
          variant="body1"
          color={'textSecondary'}
          className={clsx(classes.childrenInfo)}
        >
          {i18n.translate(`SLIDEOUT_CHILDREN_INFO`, {
            year: '20' + activeYear,
          })}
        </Typography>
        <PopStack pop={pop} />
      </PanelSection>
    )
  }

  const getRedliningData = () => {
    if (!redliningActive || isZip || isCounty) return
    // display tract as 100% ungraded if no data
    const redliningGrades =
      remoteJson.redliningBreakdown.data[feature.id] || {}
    const sortedGrades = getSortedGrades(redliningGrades)
    const displayRoundedSumNote =
      _.sumBy(sortedGrades, 'ratio') !== 1

    return (
      <PanelSection>
        <Typography variant="h2">
          {i18n.translate(`LEGEND_REDLINING`)}
        </Typography>
        <Typography
          variant="body1"
          color={'textSecondary'}
          className={clsx(classes.childrenInfo)}
        >
          {i18n.translate(`REDLINING_CLASSIFICATION`)}
        </Typography>
        <GenericStack items={sortedGrades} />
        {displayRoundedSumNote && (
          <Typography
            variant="body1"
            color={'textSecondary'}
            className={clsx(classes.roundedSumNote)}
          >
            {i18n.translate(`ROUNDED_SUM`)}
          </Typography>
        )}
      </PanelSection>
    )
  }

  const getDomainSection = (
    domain,
    withoutContent = false,
    noTooltip = false,
  ) => {
    const level =
      feature.properties['x' + domain.key + activeNorm]
    const score =
      feature.properties['s' + domain.key + activeNorm]

    if (_.isNaN(parseInt(level))) return null
    // TODO (ETL): correct instead in source csv in ETL
    const domainName = domain.domain_name
      .split(' ')
      .map(w =>
        w === 'and' ? w : capitalizeFirstLetter(w),
      )
      .join(' ')
    const IndexWithRatingComponent = (
      <IndexWithRating
        key={domain.key}
        name={domainName}
        score={score}
        level={level}
        activeGeoType={activeGeoType}
        // TODO: standardize language around domain vs index vs metric
        index={domain.key}
        variabilityLevel={
          (isZip || isCounty) &&
          getVaribilityLevel({
            metric: domain.key,
            activeNorm,
            feature,
          })
        }
        tooltipText={
          !noTooltip &&
          i18n.translate(`SLIDEOUT_TIP_${domain.key}`)
        }
      />
    )

    if (withoutContent) {
      return (
        <Box
          key={domain.key}
          className={classes.unexpandableIndex}
        >
          {IndexWithRatingComponent}
        </Box>
      )
    }
    return (
      <Accordion
        id={domain.key}
        key={domain.key}
        className={classes.indexSection}
        summary={IndexWithRatingComponent}
        details={<DomainDetailsSection domain={domain} />}
      />
    )
  }

  const geoType = i18n.translate(
    `GEO_TYPE_SIMPLE_${activeGeoType.toUpperCase()}`,
  )
  const geoTypeSingular = i18n.translate(
    `GEO_TYPE_SIMPLE_SINGULAR_${activeGeoType.toUpperCase()}`,
  )

  const DomainSections = indexMap.domains.map(d =>
    getDomainSection(d),
  )
  const partialData = DomainSections.some(_.isNil)

  const getOpportunityLevels = () => {
    return (
      <PanelSection>
        <Typography variant="h2">
          {i18n.translate(`SLIDEOUT_HEADING_X`)}
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          className={classes.comparedTo}
          dangerouslySetInnerHTML={{
            __html: i18n.translate(`SLIDEOUT_COMPARE_TIP`, {
              geoType,
              thisPhrase,
              year: '20' + activeYear,
            }),
          }}
        />
        {(isZip || isCounty) && (
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.indicatorExplainer}
            dangerouslySetInnerHTML={{
              __html: i18n.translate(
                `SLIDEOUT_NO_INDICATORS`,
                {
                  geoType: geoTypeSingular,
                },
              ),
            }}
          />
        )}
        {partialData && (
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.indicatorExplainer}
            dangerouslySetInnerHTML={{
              __html: i18n.translate(
                `SLIDEOUT_PARTIAL_DATA`,
                {
                  geoType: geoTypeSingular,
                },
              ),
            }}
          />
        )}
        <Box className={classes.indexScores}>
          {getDomainSection(
            {
              key: COI_METRIC,
              domain_name: indexMap.indicator_name,
            },
            // no indicators for COI, but county has variability data
            !isCounty,
            true,
          )}
          {DomainSections}
        </Box>
      </PanelSection>
    )
  }

  return (
    <Box className={clsx('panel-root', classes.root)}>
      <PanelHeader
        title={i18n.translate(`PANEL_LOCATION_TITLE`)}
      ></PanelHeader>
      <PanelBody>
        {getLocDetails()}
        {getPopulationBreakdown()}
        {getOpportunityLevels()}
        {getRedliningData()}
      </PanelBody>
    </Box>
  )
}

export default LocationDetailsPanel
